/**
 * /l/:token — one-time sign-in link from a WhatsApp notification.
 * Redeemed by POST from here (not by the GET that WhatsApp's link preview
 * makes), then straight to the page the notification pointed at. A used or
 * expired link still takes you there: directly if you're already signed in,
 * otherwise via the login page.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function MagicLink() {
  const { token } = useParams();
  const nav = useNavigate();
  const { acceptSession } = useAuth();
  const [msg, setMsg] = useState("Signing you in…");
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;   // never spend the token twice (StrictMode / re-render)
    done.current = true;
    api.post("/auth/link/redeem", { token })
      .then((r) => {
        acceptSession(r.data);
        nav(r.data.path || "/app/dashboard", { replace: true });
      })
      .catch((err) => {
        const d = err?.response?.data?.detail || {};
        const path = (typeof d === "object" && d.path) || "/app/dashboard";
        if (localStorage.getItem("sw_token")) {
          nav(path, { replace: true });
          return;
        }
        setMsg("This link was already used or has expired.");
        toast.message("This link works once — please sign in to continue.");
        setTimeout(() => nav(`/login?next=${encodeURIComponent(path)}`, { replace: true }), 1200);
      });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen grid place-items-center bg-[var(--sw-bg)] p-6" data-testid="magic-link-page">
      <div className="text-center">
        <Loader2 className="animate-spin mx-auto text-slate-400 mb-3" />
        <div className="text-[14px] text-slate-600">{msg}</div>
      </div>
    </div>
  );
}
